import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName, Skeleton } from '../../../../components';
import { IntervalDropdown } from '../../../home/components';
import { usePeriodPicker } from '../../../home/usePeriodPicker';
import type { HomePeriod, Kpi, KpiStat } from '../../../../mock-data/types';
import {
  Root,
  Header,
  H2,
  Grid,
  Kpi as KpiCard,
  Head,
  Title,
  Sub,
  Body,
  ColMain,
  VRule,
  Label,
  Value,
  Delta,
  DeltaVs,
  Trend,
  TrendText,
  type DeltaTone,
} from './Performance.styles';

const TONE_ICON: Record<DeltaTone, IconName> = {
  up: 'trending-up',
  down: 'trending-down',
  flat: 'minus',
};

/** What a column with no figure puts in its delta pill: the same `–` the figure shows,
 *  because there is nothing to compare either. It stands alone — the flat tone's own
 *  glyph is a dash too, and the pair rendered as "– –", which reads as a fault. */
const NO_DELTA = '–';

/**
 * The comparison against the previous window. Three tones, because a delta can also fail
 * to point anywhere: `flat` covers a figure that held steady (`0%`, which a green rising
 * pill would otherwise dress up as growth) and a marker column, which has no comparison to
 * make at all and says so with the same `–` as its figure.
 *
 * Every column renders one, in every state. That is deliberate: the pill and its caption
 * are what give the inner panel its height, and the panel is bottom-anchored inside a
 * stretched grid cell — so a column that omitted the row would not just lose a line, it
 * would drag the whole panel down out of line with its neighbours.
 */
const DeltaPill = ({ stat, glyph }: { stat: KpiStat; glyph: string | null }): React.ReactElement => {
  const tone: DeltaTone = glyph || stat.flat ? 'flat' : stat.changeType === 'decrease' ? 'down' : 'up';
  return (
    <Trend $tone={tone}>
      {glyph ? null : <Icon name={TONE_ICON[tone]} size={11} />}
      <TrendText text={glyph ? NO_DELTA : stat.change} tag="span" />
    </Trend>
  );
};

export type PerformanceProps = {
  kpis: Kpi[];
  /** The selected range, same options + logic as Home/Stats. Controlled by the page. */
  range: HomePeriod;
  onRangeChange: (range: HomePeriod) => void;
  /** Left (selected-range) column heading, e.g. "Today", "Last 30 days". */
  leftLabel: string;
  /** Left-column delta caption, e.g. "vs yesterday", "vs last month". */
  leftCaption: string;
  /** Right ("next step up" range) column heading, e.g. "This week", "Last 60 days". */
  rightLabel: string;
  /** Right-column delta caption, e.g. "vs last week", "vs previous 60 days". */
  rightCaption: string;
  /** While true, every tile's figures + delta pills show placeholders (the endpoint is loading). */
  loading?: boolean;
};

// The value text is 24px (Performance.styles `Value`); ~12px per glyph sizes a
// placeholder close to the eventual figure so the tile doesn't shift on reveal.
const valueWidth = (text: string): number => Math.max(44, text.length * 12);

// A figure with no real value renders a marker instead of a number (the same
// convention as Home/Stats): `?` not wired to an API field (the ad-spend KPIs),
// `N/A` the request is failing, `–` the value is undefined this period (a ratio
// dividing by zero, e.g. a per-FTD figure with zero FTDs). None of them has a
// comparison either, so its delta pill goes flat (see `DeltaPill`). `null` means a
// normal numeric figure.
const stateGlyph = (stat: KpiStat): string | null =>
  stat.missing ? '?' : stat.error ? 'N/A' : stat.indeterminate ? '–' : null;

/** Bottom KPI grid: each tile shows the metric over TWO windows, a LEFT column for
 *  the selected range and a RIGHT column for the "next step up" range (source 6466-…).
 *  The header range selector (harmonised with Home/Stats) drives BOTH columns, which
 *  are live per-brand data from the Stats API (see `useAcquisitionPerformance`). The
 *  titles + the per-column heading/caption are static chrome; the figures + delta
 *  pills load in. */
export const Performance = ({
  kpis,
  range,
  onRangeChange,
  leftLabel,
  leftCaption,
  rightLabel,
  rightCaption,
  loading = false,
}: PerformanceProps): React.ReactElement => {
  const { t } = useTranslation();

  // Same picker as Home/Stats: the presets plus a custom run of days.
  const picker = usePeriodPicker(range, onRangeChange);

  // The KPI titles + subtitles are a fixed, closed UI vocabulary (the figures and
  // deltas are the per-brand data). They reach this section as dynamic props from
  // the data source, so translate them at the render site over the known set; an
  // unrecognised value renders as-is.
  const kpiTitle = (title: string): string => {
    const labels: Record<string, string> = {
      'Blended CPA': t('Blended CPA'),
      'Budget spent': t('Budget spent'),
      Registrations: t('Registrations'),
      FTDs: t('FTDs'),
      'FTD Deposits': t('FTD Deposits'),
      'Avg Deposit / FTD': t('Avg Deposit / FTD'),
      'Total Turnover': t('Total Turnover'),
      'Turnover / FTD': t('Turnover / FTD'),
      GGR: t('GGR'),
      ROI: t('ROI'),
    };
    return labels[title] ?? title;
  };
  const kpiSubtitle = (subtitle: string): string => {
    const labels: Record<string, string> = {
      'Cost per acquisition': t('Cost per acquisition'),
      'Total acquisition spend': t('Total acquisition spend'),
      'New players joining your website': t('New players joining your website'),
      'Number of First Time Deposits': t('Number of First Time Deposits'),
      'Total deposited by FTDs': t('Total deposited by FTDs'),
      'Average first deposit': t('Average first deposit'),
      'Money wagered by players': t('Money wagered by players'),
      'Wagered per depositor': t('Wagered per depositor'),
      'Gross Gaming Revenue': t('Gross Gaming Revenue'),
      'Return on ad spend': t('Return on ad spend'),
    };
    return labels[subtitle] ?? subtitle;
  };

  return (
    <Root>
      <Header>
        <H2 text="Performance Overview" tag="span" />
        <IntervalDropdown value={range} options={picker.options} onChange={onRangeChange} custom={picker.custom} />
      </Header>
      <Grid>
        {kpis.map((kpi) => {
          // Marker figures (`?` not wired, `N/A` failing, `–` undefined this period) take
          // precedence over the loading skeleton, same rule as Home's StatValue. Each
          // column resolves its own marker (the windows can differ).
          const todayGlyph = stateGlyph(kpi.today);
          const monthGlyph = stateGlyph(kpi.month);
          return (
            <KpiCard key={kpi.id}>
              <Head>
                <div>
                  <Title text={kpiTitle(kpi.title)} tag="span" />
                  <Sub text={kpiSubtitle(kpi.subtitle)} tag="span" />
                </div>
              </Head>
              <Body>
                <ColMain>
                  <Label text={leftLabel} tag="span" />
                  {todayGlyph ? (
                    <Value text={todayGlyph} tag="span" />
                  ) : loading ? (
                    <Skeleton width={valueWidth(kpi.today.value)} height={22} radius={6} />
                  ) : (
                    <Value text={kpi.today.value} tag="span" />
                  )}
                  <Delta>
                    {loading && !todayGlyph ? (
                      <Skeleton width={52} height={18} radius={7} />
                    ) : (
                      <DeltaPill stat={kpi.today} glyph={todayGlyph} />
                    )}
                    <DeltaVs text={leftCaption} tag="span" />
                  </Delta>
                </ColMain>
                <VRule />
                <ColMain $right>
                  <Label text={rightLabel} tag="span" />
                  {monthGlyph ? (
                    <Value text={monthGlyph} tag="span" />
                  ) : loading ? (
                    <Skeleton width={valueWidth(kpi.month.value)} height={22} radius={6} />
                  ) : (
                    <Value text={kpi.month.value} tag="span" />
                  )}
                  <Delta $right>
                    {loading && !monthGlyph ? (
                      <Skeleton width={52} height={18} radius={7} />
                    ) : (
                      <DeltaPill stat={kpi.month} glyph={monthGlyph} />
                    )}
                    <DeltaVs text={rightCaption} tag="span" />
                  </Delta>
                </ColMain>
              </Body>
            </KpiCard>
          );
        })}
      </Grid>
    </Root>
  );
};

export default Performance;
